/*
 * File: user.slice.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 17, 2024 at 10:51 PM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: April 18, 2025 at 1:04 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import Hotjar from '@hotjar/browser';

// Services
import { getCurrentUser } from '../../../services/user';

// Constants
import { ROLES } from '../../../../constants/base.constants';

// State
const isDevelopment = process.env.ENV === 'development';
const isProduction = process.env.ENV === 'production';


/**
 * Constants
 */

const defaultUser = isDevelopment ? {
	is_active: false,
	active_session: false,
	is_anonymous: true,
	roles: [ROLES.STANDARD],
	primary_role: ROLES.STANDARD,
} : undefined;


/**
 * Functions
 */

export const setUserForAnalytics = (user) => {

	// Only set user for analytics if not impersonated
	if (user && !user.is_impersonation) {

		// Set current user to Sentry
		Sentry.setUser({
			id: user.id,
			username: user.username || undefined,
			email: user.email || undefined,
			ip_address: '{{auto}}'
		});

		// Set Hotjar user
		if (isProduction && Hotjar.isReady()) {
			Hotjar.identify(user.id, {
				username: user.username || undefined,
				email: user.email || undefined,
			});
		}

		// Set current user for GTM
		try {
			window.dataLayer.push({ user_id: user.id });
		} catch (_) {}
	}
};

export const fetchUser = createAsyncThunk('user/fetchUser', async () => {

	// Fetch current user
	const { data: { result } } = await getCurrentUser();

	// Return user
	return result.user;
});


/**
 * Slices
 */

export const userSlice = createSlice({
	name: 'user',
	initialState: {
		status: 'idle',
		error: null,
		value: defaultUser,
		impersonationInProgress: false
	},
	reducers: {
		updateUser: (state, action) => {

			// Set state
			state.value = action.payload;
			state.status = 'final';

			// Set current user for analytics
			setUserForAnalytics(action.payload);
		},
		clearUser: (state) => {

			// Clear state
			state.status = 'final';
			state.value = defaultUser;

			// Clear current user from Sentry
			Sentry.configureScope((scope) => scope.setUser(null));

			// Clear current user from GTM
			try {
				window.dataLayer.push({ user_id: undefined });
			} catch (_) {}
		},
		updateImpersonationProgress: (state, action) => {

			// Set state
			state.impersonationInProgress = action.payload;
		}
	},
	extraReducers: {
		[fetchUser.pending]: (state) => {
			state.status = 'loading';
		},
		[fetchUser.fulfilled]: (state, action) => {

			// Set status
			state.status = 'final';

			// Set value
			state.value = action.payload;

			// Set current user for analytics
			setUserForAnalytics(action.payload);
		},
		[fetchUser.rejected]: (state, action) => {
			state.status = 'failed';
			state.error = action.error.message;
		},
	},
});


/**
 * Exports
 */

export const { updateUser, clearUser, updateImpersonationProgress } = userSlice.actions;

export default userSlice.reducer;
